import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { CheckboxField, InputField } from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { Tables } from "../../../gen/tableNames/tables";
import { Clients } from "../../../lib/clients";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { parseYes } from "@/features/FormCard/utils";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

const FormSchema = z.object({
  portfolio_id: z.coerce.number().nullable().optional(),
  needs_lookthrough: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),
});

export function G4MetadataNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client: Clients.G4,
    table_name: Tables.G4_METADATA,
    FormSchema,
    isNew: true,
  });

  const { t } = useTranslation();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.G4_METADATA}.new_metadata`, {
                  ns: "tables",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name="portfolio_id"
                    label={t(`${Tables.G4_METADATA}.columns.portfolio_id`, {
                      ns: "tables",
                    })}
                    control={form.control}
                    isRequired
                  />
                  <CheckboxField
                    name="needs_lookthrough"
                    label={t(
                      `${Tables.G4_METADATA}.columns.needs_lookthrough`,
                      {
                        ns: "tables",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, Tables.G4_METADATA)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
